import {
  NJInlineMessage,
  NJSpinner,
} from "@engie-group/fluid-design-system-react";
import { useState } from "react";
import type { Session } from "@/types";
import { AssistantMessage } from "./AssistantMessage";
import { MarkdownMessage } from "./MarkdownMessage";
import { useChat } from "./ChatProvider";
import { SourcesPanel } from "./SourcesPanel";
import type { ChatMessage, ChatSource } from "./types";

function formatWhen(at: string): string {
  if (!at) return "";
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

function BubbleHeader({ who, at }: { who: string; at: string }) {
  const when = formatWhen(at);
  return (
    <div className="mb-1 flex items-center gap-2 text-xs text-nj-neutral-secondary-default">
      <strong>{who}</strong>
      {when && <span>{when}</span>}
    </div>
  );
}

function UserBubble({ content, at }: { content: string; at: string }) {
  return (
    <div className="flex justify-end py-1">
      <div
        className="max-w-[80%] border px-3 py-2 text-nj-neutral-primary-default"
        style={{
          backgroundColor:
            "var(--nj-semantic-color-background-brand-secondary-default)",
          borderColor: "var(--nj-semantic-color-border-brand-subtle-default)",
        }}
      >
        <BubbleHeader who="You" at={at} />
        <MarkdownMessage content={content} />
      </div>
    </div>
  );
}

/** Latest agent tool-progress label from the SSE `agent_event` stream (real agents only). */
function agentActivityLabel(events: unknown[]): string | null {
  const last = events[events.length - 1] as
    { tool?: string; type?: string; preview?: string } | undefined;
  if (!last) return null;
  if (last.preview) return last.preview;
  if (last.tool) return `Using ${last.tool}…`;
  if (last.type) return `${last.type}…`;
  return null;
}

/** Conversation thread for a session — history + incremental SSE streaming (shared via the chat
 * provider); the composer is docked separately in the app shell. */
export function ChatWindow({
  session,
  agentName,
}: {
  session: Session;
  agentName: string;
}) {
  const { isStreaming, streamedText, agentEvents, pendingUserMessage } =
    useChat();
  const [openSources, setOpenSources] = useState<ChatSource[] | null>(null);
  const activity = isStreaming ? agentActivityLabel(agentEvents) : null;
  const isClosed = session.status !== "ACTIVE";

  // Show the just-sent message immediately, unless the session cache already carries it as the
  // last user message (i.e. `done` has landed and repopulated the thread).
  const messages = session.messages ?? [];
  const lastMessage = messages[messages.length - 1];
  const showPendingUser =
    pendingUserMessage !== null &&
    !(
      lastMessage?.role === "user" && lastMessage.content === pendingUserMessage
    );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        {messages.map((message) => {
          const chatMessage = message as ChatMessage;
          return message.role === "user" ? (
            <UserBubble
              key={message.id}
              content={message.content}
              at={message.at}
            />
          ) : (
            <AssistantMessage
              key={message.id}
              message={chatMessage}
              agentName={agentName}
              onOpenSources={setOpenSources}
            />
          );
        })}

        {showPendingUser ? (
          <UserBubble content={pendingUserMessage} at="" />
        ) : null}

        {isStreaming && (streamedText || activity) ? (
          <AssistantMessage
            message={{
              id: "streaming",
              role: "assistant",
              content: streamedText,
              at: "",
            }}
            agentName={agentName}
            streaming
            onOpenSources={setOpenSources}
          />
        ) : null}

        {isStreaming ? (
          <div className="flex items-center gap-2 px-1 text-sm text-nj-neutral-secondary-default">
            <NJSpinner size="sm" aria-label="The agent is thinking" />
            <span>{activity ?? "The agent is thinking…"}</span>
          </div>
        ) : null}
      </div>

      {isClosed ? (
        <NJInlineMessage variant="information">
          This session is closed; reopen it to continue the conversation.
        </NJInlineMessage>
      ) : null}

      {openSources && (
        <SourcesPanel
          title={`Sources (${openSources.length})`}
          sources={openSources}
          onClose={() => setOpenSources(null)}
        />
      )}
    </div>
  );
}
